import { css } from "@emotion/react";
import { MantineTheme } from "@mantine/core";
import { Editor, TraitProperties } from "grapesjs";
import { renderToStaticMarkup } from "react-dom/server";

import { HL_BASE_COMPONENT_TYPE } from "./BaseHLComponentPlugin";

const HL_COLLECTION_CARD_COMPONENT_TYPE = "hl-collection-card";

type Props = {
  collectionId: string;
  tokenId: string;
  aspectRatio: string;
  design: "default" | "artwork-only";
  // maxImageHeight: string;
};

const getComponentStyles = (theme: MantineTheme) => {
  const componentStyles = css`
    .hl-collection-card {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: stretch;
    }

    .hl-collection-card .collection-image {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-grow: 1;
      width: 100%;
      aspect-ratio: 1;
      background-color: #ededed;
      border-radius: 8px;
      border: 20px solid ${theme.colors.gray[3]};
      color: ${theme.colors.gray[5]};
    }

    .hl-collection-card .collection-body {
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding: 16px 5px;
    }

    .hl-collection-card .collection-title {
      font-size: 16px;
      font-weight: 500;
    }

    .hl-collection-card .collection-subtitle {
      font-size: 14px;
      font-weight: 500;
      color: ${theme.colors.gray[5]};
    }

    .hl-collection-card .collection-artist {
      display: flex;
      gap: 4px;
      align-items: center;
      font-size: 14px;
      font-weight: 500;
      color: ${theme.colors.gray[5]};
    }

    .hl-collection-card .collection-artist-avatar {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background-color: ${theme.colors.gray[3]};
    }

    .banana {
      background: yellow;
    }

    @media (min-width: ${theme.breakpoints.md}px) {
      // .hl-collection-card {
      //   background: blue;
      // }
    }
  `;

  return componentStyles.styles;
};

const ViewComponent = (
  props: Props & {
    theme: MantineTheme;
  }
) => {
  const {
    collectionId,
    tokenId,
    aspectRatio = "1/1",
    design = "default",
    // maxImageHeight = "",
  } = props;

  const title = tokenId == "" ? "Collection name" : `Token #${tokenId}`;
  const subtitle = tokenId == "" ? null : "Collection name";

  const body = (() => {
    if (design === "default") {
      return (
        <div className="collection-body">
          <div className="collection-title">{title}</div>
          {subtitle != null && (
            <div className="collection-subtitle">{subtitle}</div>
          )}
          {/* <div className="collection-artist">
          <div className="collection-artist-avatar"></div>
          <div>Collection artist</div>
        </div> */}
        </div>
      );
    }

    return null;
  })();

  return (
    <div className="hl-collection-card">
      <div
        className="collection-image"
        style={{
          aspectRatio,
          // maxHeight: maxImageHeight,
        }}
      >
        {collectionId}
      </div>
      {body}
    </div>
  );
};

export const HLCollectionCardPlugin =
  (theme: MantineTheme) => (editor: Editor) => {
    const { Blocks, Components } = editor;

    Components.addType(HL_COLLECTION_CARD_COMPONENT_TYPE, {
      extend: HL_BASE_COMPONENT_TYPE,
      isComponent() {
        return false;
      },
      model: {
        defaults: {
          type: HL_COLLECTION_CARD_COMPONENT_TYPE,
          name: "HL Collection Card",
          tagName: "div",
          attributes: {
            "data-gjs-type": HL_COLLECTION_CARD_COMPONENT_TYPE,
          },
          traits: [
            {
              type: "text",
              name: "collectionId",
              label: "Collection ID",
              default: "Enter the collection ID",
              required: true,
              changeProp: true,
            },
            {
              type: "text",
              name: "tokenId",
              label: "Token ID (optional)",
              default: "Enter the token ID",
              changeProp: true,
            },
            {
              type: "select",
              name: "aspectRatio",
              label: "Aspect Ratio",
              default: "1/1",
              changeProp: true,
              options: [
                { id: "1/1", label: "Square" },
                // { id: "2/1", label: "2:1" },
                { id: "16/9", label: "16:9" },
                { id: "4/3", label: "4:3" },
                // { id: "3/2", label: "3:2" },
              ],
            },
            {
              type: "select",
              name: "design",
              label: "Design",
              default: "default",
              changeProp: true,
              options: [
                { id: "default", label: "Default" },
                { id: "artwork-only", label: "Artwork only" },
              ],
            },
            // {
            //   type: "number",
            //   name: "maxImageHeight",
            //   label: "Max image height",
            //   units: ["px"],
            //   min: 0,
            //   default: "auto",
            //   changeProp: true,
            // },
          ] as TraitProperties[],
          stylable: [],
          // Traits from changeProp: true
          collectionId: "664726dd129077665ac20d40",
          tokenId: "1",
          aspectRatio: "1/1",
          design: "default",
          // maxImageHeight: "",
        },
      },
      view: {
        onRender({ el, model }) {
          console.log("HLCollectionCard [onRender]", { el, model });
          //@ts-ignore
          const props = this.model.toJSONData();
          el.innerHTML = renderToStaticMarkup(
            <ViewComponent {...props} theme={theme} />
          );
        },
      },
    });

    Blocks.add(HL_COLLECTION_CARD_COMPONENT_TYPE, {
      label: "Collection Card",
      category: "Highlight",
      content: { type: HL_COLLECTION_CARD_COMPONENT_TYPE },
    });
  };

HLCollectionCardPlugin.type = HL_COLLECTION_CARD_COMPONENT_TYPE;
HLCollectionCardPlugin.getStyles = getComponentStyles;
